import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Search, CheckCircle2, XCircle, MessageSquare, Send, Inbox } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminSmsOptins — Twilio TFN compliance audit dashboard.
 *
 * Mirrors the AdminST1Filings structure: summary card up top, filter-by-phone
 * search, three tabs (opt-ins, inbound, outbound) each backed by their own
 * read-only endpoint at /api/admin/sms/*.
 *
 * Intended use: quick answer to "did user X opt in? when? on which IP? are
 * they still opted in? did they text STOP? did any messages get sent to them
 * after opt-out?" without ever leaving the browser.
 */
export default function AdminSmsOptins({ user, onLogout }) {
  const [summary, setSummary] = useState(null);
  const [optins, setOptins] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [outbound, setOutbound] = useState([]);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [sum, oi, ib, ob] = await Promise.all([
        api.get('/admin/sms/compliance-summary'),
        api.get('/admin/sms/optins', { params: phoneFilter ? { phone: phoneFilter } : {} }),
        api.get('/admin/sms/inbound-log', { params: phoneFilter ? { phone: phoneFilter } : {} }),
        api.get('/admin/sms/outbound-log', { params: phoneFilter ? { phone: phoneFilter } : {} }),
      ]);
      setSummary(sum.data);
      setOptins(oi.data.rows || []);
      setInbound(ib.data.rows || []);
      setOutbound(ob.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load SMS data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // load once on mount; explicit "Refresh" & "Search" for later reloads

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-sms-optins-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SMS Opt-Ins · Compliance Audit</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every consumer SMS event captured for Twilio TFN compliance. Use the search box to
            focus on a single phone number — helpful when a carrier or regulator asks for
            evidence of a specific consumer&apos;s consent history.
          </p>
        </div>

        <ComplianceSummaryCard summary={summary} />

        <Card>
          <CardContent className="pt-6 flex gap-3 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input
                placeholder="Search by phone (e.g. 5715551234 or +1703…)"
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                className="pl-9"
                data-testid="admin-sms-search-input"
              />
            </div>
            <Button onClick={load} disabled={loading} data-testid="admin-sms-search-button">
              <Search className="w-4 h-4 mr-2" />Search
            </Button>
            <Button variant="outline" onClick={() => { setPhoneFilter(''); setTimeout(load, 0); }}
                    disabled={loading} data-testid="admin-sms-clear-button">
              Clear
            </Button>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading}
                    data-testid="admin-sms-refresh-button" className="ml-auto">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </CardContent>
        </Card>

        <Tabs defaultValue="optins">
          <TabsList>
            <TabsTrigger value="optins" data-testid="admin-sms-tab-optins">
              <MessageSquare className="w-4 h-4 mr-2" />Opt-ins · {optins.length}
            </TabsTrigger>
            <TabsTrigger value="inbound" data-testid="admin-sms-tab-inbound">
              <Inbox className="w-4 h-4 mr-2" />Inbound · {inbound.length}
            </TabsTrigger>
            <TabsTrigger value="outbound" data-testid="admin-sms-tab-outbound">
              <Send className="w-4 h-4 mr-2" />Outbound · {outbound.length}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="optins"><OptinsTable rows={optins} /></TabsContent>
          <TabsContent value="inbound"><InboundTable rows={inbound} /></TabsContent>
          <TabsContent value="outbound"><OutboundTable rows={outbound} /></TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

function ComplianceSummaryCard({ summary }) {
  if (!summary) return (
    <Card><CardContent className="py-6 text-sm text-slate-500">Loading summary…</CardContent></Card>
  );
  const boxes = [
    { label: 'Active opt-ins', value: summary.active_optins, tone: 'emerald', testid: 'sms-summary-active' },
    { label: 'Opted out', value: summary.opted_out, tone: 'slate', testid: 'sms-summary-optout' },
    { label: 'Outbound sent', value: summary.outbound_sent, tone: 'blue', testid: 'sms-summary-sent' },
    { label: 'Outbound failed', value: summary.outbound_failed, tone: 'red', testid: 'sms-summary-failed' },
    { label: 'STOP replies', value: summary.stop_replies, tone: 'amber', testid: 'sms-summary-stop' },
    { label: 'HELP replies', value: summary.help_replies, tone: 'slate', testid: 'sms-summary-help' },
    { label: 'START replies', value: summary.start_replies, tone: 'emerald', testid: 'sms-summary-start' },
    { label: 'Inbound total', value: summary.inbound_total, tone: 'slate', testid: 'sms-summary-inbound' },
  ];
  const toneClass = (t) => ({
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
  }[t] || 'bg-slate-50 border-slate-200');

  return (
    <Card data-testid="admin-sms-compliance-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-500 font-normal">
          Compliance summary · as of {new Date(summary.as_of).toLocaleString()}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {boxes.map(b => (
            <div key={b.label}
                 className={`border rounded p-3 ${toneClass(b.tone)}`}
                 data-testid={b.testid}>
              <div className="text-xs opacity-70">{b.label}</div>
              <div className="text-2xl font-semibold">{b.value}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OptinsTable({ rows }) {
  if (rows.length === 0) return (
    <Card className="mt-4"><CardContent className="py-8 text-center text-slate-500">No opt-in rows match.</CardContent></Card>
  );
  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        <table className="w-full text-sm" data-testid="admin-sms-optins-table">
          <thead className="text-left text-xs text-slate-500 border-b">
            <tr>
              <th className="py-2 pr-3">Phone</th>
              <th className="pr-3">Status</th>
              <th className="pr-3">Opt-in at</th>
              <th className="pr-3">Opt-out at</th>
              <th className="pr-3">Source</th>
              <th className="pr-3">IP</th>
              <th className="pr-3">User agent</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100"
                  data-testid={`admin-sms-optin-row-${i}`}>
                <td className="py-2 pr-3 font-mono">{r.phone}</td>
                <td className="pr-3">
                  {r.opted_out_at
                    ? <Badge className="bg-slate-500" data-testid={`sms-status-badge-${i}`}><XCircle className="w-3 h-3 mr-1" />Opted Out</Badge>
                    : (r.consent
                        ? <Badge className="bg-emerald-500" data-testid={`sms-status-badge-${i}`}><CheckCircle2 className="w-3 h-3 mr-1" />Opted In</Badge>
                        : <Badge className="bg-amber-500" data-testid={`sms-status-badge-${i}`}>No Consent</Badge>)}
                </td>
                <td className="pr-3 text-xs">{r.opt_in_at ? new Date(r.opt_in_at).toLocaleString() : '—'}</td>
                <td className="pr-3 text-xs">{r.opted_out_at ? new Date(r.opted_out_at).toLocaleString() : '—'}</td>
                <td className="pr-3 text-xs text-slate-500">{r.source || r.opt_out_source || '—'}</td>
                <td className="pr-3 text-xs font-mono text-slate-500">{r.ip_address || '—'}</td>
                <td className="pr-3 text-xs text-slate-500 max-w-xs truncate" title={r.user_agent || ''}>{r.user_agent || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function InboundTable({ rows }) {
  if (rows.length === 0) return (
    <Card className="mt-4"><CardContent className="py-8 text-center text-slate-500">No inbound messages match.</CardContent></Card>
  );
  const badge = (k) => ({
    STOP: 'bg-amber-500', START: 'bg-emerald-500', HELP: 'bg-blue-500', OTHER: 'bg-slate-400',
  }[k] || 'bg-slate-400');
  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        <table className="w-full text-sm" data-testid="admin-sms-inbound-table">
          <thead className="text-left text-xs text-slate-500 border-b">
            <tr>
              <th className="py-2 pr-3">From</th>
              <th className="pr-3">Body</th>
              <th className="pr-3">Classified</th>
              <th className="pr-3">Twilio MessageSid</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100"
                  data-testid={`admin-sms-inbound-row-${i}`}>
                <td className="py-2 pr-3 font-mono">{r.from}</td>
                <td className="pr-3 max-w-md truncate" title={r.body}>{r.body}</td>
                <td className="pr-3"><Badge className={badge(r.classified_as)}>{r.classified_as}</Badge></td>
                <td className="pr-3 font-mono text-xs text-slate-500">{r.twilio_message_sid || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function OutboundTable({ rows }) {
  if (rows.length === 0) return (
    <Card className="mt-4"><CardContent className="py-8 text-center text-slate-500">No outbound messages match.</CardContent></Card>
  );
  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        <table className="w-full text-sm" data-testid="admin-sms-outbound-table">
          <thead className="text-left text-xs text-slate-500 border-b">
            <tr>
              <th className="py-2 pr-3">To</th>
              <th className="pr-3">Result</th>
              <th className="pr-3">Category</th>
              <th className="pr-3">Body</th>
              <th className="pr-3">Sent at</th>
              <th className="pr-3">Twilio SID / Error</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100"
                  data-testid={`admin-sms-outbound-row-${i}`}>
                <td className="py-2 pr-3 font-mono">{r.to}</td>
                <td className="pr-3">
                  {r.ok
                    ? <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" />Sent</Badge>
                    : <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>}
                </td>
                <td className="pr-3 text-xs text-slate-500">{r.category || '—'}</td>
                <td className="pr-3 max-w-xs truncate" title={r.body}>{r.body}</td>
                <td className="pr-3 text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                <td className="pr-3 font-mono text-xs text-slate-500">
                  {r.ok ? (r.twilio_sid || '—') : <span className="text-red-700">{r.error || '—'}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
